// =====================================================================
// Aba 5 — Serviços (Fase 3, S38).
// CRUD clinic_services. Campos required: service_key (slug), name.
// =====================================================================
import React from "react";
import { Chip } from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import StarIcon from "@material-ui/icons/Star";

import CrudTable from "../components/CrudTable";
import CrudDialog from "../components/CrudDialog";
import ConfirmDeleteDialog from "../components/ConfirmDeleteDialog";
import useCrudResource from "../hooks/useCrudResource";

import {
  listServices,
  createService,
  updateService,
  deleteService
} from "../../../services/clinicAdminService";

const COLUMNS = [
  { key: "name", label: "Nome" },
  {
    key: "service_key",
    label: "Chave",
    width: 180,
    render: (r) => <code>{r.service_key}</code>
  },
  {
    key: "category",
    label: "Categoria",
    width: 140,
    render: (r) => r.category || "—"
  },
  {
    key: "price_text",
    label: "Preço",
    width: 140,
    render: (r) => r.price_text || (r.price ? `R$ ${Number(r.price).toFixed(2)}` : "—")
  },
  {
    key: "is_active",
    label: "Ativo",
    width: 70,
    align: "center",
    render: (r) =>
      r.is_active ? (
        <CheckCircleIcon fontSize="small" htmlColor="#06c270" />
      ) : (
        "—"
      )
  },
  {
    key: "is_featured",
    label: "Destaque",
    width: 90,
    align: "center",
    render: (r) =>
      r.is_featured ? <StarIcon fontSize="small" htmlColor="#ff9800" /> : "—"
  },
  {
    key: "sort_order",
    label: "Ordem",
    width: 80,
    align: "right",
    render: (r) => r.sort_order ?? "—"
  }
];

const FIELDS = [
  {
    key: "service_key",
    label: "Chave (slug)",
    type: "text",
    required: true,
    colSpan: 6,
    regex: /^[a-z0-9_-]+$/i,
    regexError: "Apenas letras, números, _ e -",
    placeholder: "consulta_avaliacao",
    helperText: "Identificador único (max 64). Imutável após criação."
  },
  { key: "name", label: "Nome", type: "text", required: true, colSpan: 6 },
  {
    key: "category",
    label: "Categoria",
    type: "text",
    colSpan: 6,
    placeholder: "Consultas, Cirurgias, Estética"
  },
  {
    key: "target_audience",
    label: "Público alvo",
    type: "text",
    colSpan: 6,
    placeholder: "Adultos, gestantes…"
  },
  {
    key: "short_description",
    label: "Descrição curta",
    type: "textarea",
    rows: 2,
    helperText: "Até 512 caracteres. Usada na lista da agenda."
  },
  {
    key: "long_description",
    label: "Descrição completa",
    type: "textarea",
    rows: 4
  },
  {
    key: "price",
    label: "Preço (R$)",
    type: "number",
    colSpan: 4,
    step: 0.01,
    transform: (v) => (v === "" ? null : Number(v))
  },
  {
    key: "price_text",
    label: "Texto do preço",
    type: "text",
    colSpan: 8,
    placeholder: "R$ 350,00 (consulta + avaliação)"
  },
  { key: "is_active", label: "Ativo", type: "switch", colSpan: 4 },
  { key: "is_featured", label: "Destaque", type: "switch", colSpan: 4 },
  {
    key: "sort_order",
    label: "Ordem",
    type: "number",
    colSpan: 4,
    transform: (v) => (v === "" ? null : Number(v))
  }
];

const ServicosTab = ({ slug }) => {
  const crud = useCrudResource({
    slug,
    list: listServices,
    create: createService,
    update: updateService,
    remove: deleteService,
    resourceLabel: "serviço"
  });

  return (
    <>
      <CrudTable
        columns={COLUMNS}
        rows={crud.rows}
        loading={crud.loading}
        onAdd={crud.handleAdd}
        onEdit={crud.handleEdit}
        onDelete={crud.handleDelete}
        searchPlaceholder="Buscar serviço…"
        searchFields={["name", "service_key", "category"]}
      />

      <CrudDialog
        open={crud.dialogOpen}
        onClose={crud.handleCloseDialog}
        onSave={crud.handleSave}
        title={crud.dialogInitial ? "Editar serviço" : "Novo serviço"}
        fields={FIELDS}
        initialValues={crud.dialogInitial}
        saving={crud.saving}
      />

      <ConfirmDeleteDialog
        open={!!crud.deleteTarget}
        onClose={crud.handleCancelDelete}
        onConfirm={crud.handleConfirmDelete}
        itemLabel={crud.deleteTarget?.name}
        saving={crud.saving}
      />
    </>
  );
};

export default ServicosTab;
